import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import UserRole from "../../schemas/user/roles";
import {deleteFetcher, getFetcher, postFetcher, putFetcher} from "../../utils/api/fetchers";
import {UserAccount, userAccountSchema} from "../../schemas/user/userAccountSchemas";
import {Box, Button, Chip, Paper, Stack, TextField, Typography} from "@mui/material";
import RolesSelector from "../../components/input/RolesSelector";
import useSWR from "swr";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import {useAuth} from "../../utils/hooks/useAuth";
import SettingsIcon from '@mui/icons-material/Settings';
import {useConfirm} from "material-ui-confirm";
import {updateRoles} from "../../utils/users/updateRoles";

interface UserEditProps {

}

const UserDetails: React.FC<UserEditProps> = () => {
    const {hasPermission} = useAuth();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const {userId} = useParams<{ userId: string }>();
    const {showNotification} = useNotification();
    const {data: user, isLoading, error, mutate} = useSWR<UserAccount>(`/users/${userId}`, getFetcher);
    const {register, handleSubmit, reset, formState: {errors, isDirty}} = useForm<UserAccount>({
        defaultValues: user,
        resolver: zodResolver(userAccountSchema),
    });
    const [roles, setRoles] = useState<UserRole[]>([]);
    const [enabled, setEnabled] = useState<boolean>(false);

    const [editMode, setEditMode] = useState<boolean>(false);
    const readOnly = !hasPermission(UserRole.ADMIN);
    const noEdit = readOnly || !editMode;

    useEffect(() => {
        if (user) {
            reset(user);
            setRoles(user.roles);
            setEnabled(user.enabled);
        }
    }, [reset, user]);

    const resetData = () => {
        if (user) {
            reset(user);
            setRoles(user.roles);
            setEnabled(user.enabled);
        }
    };

    const onSubmit = async (data: UserAccount) => {
        await updateRoles(data, roles, showNotification);
        updateEnabled();

        if (!isDirty) {
            showNotification('Žádná změna v údajích nebyla provedena', 'info');
            setEditMode(false);
            return;
        }

        putFetcher(`/users/${userId}`, data)
            .then(() => {
                showNotification('Uživatel byl úspěšně upraven', 'success');
                mutate();
                setEditMode(false);
            })
            .catch((err) => {
                if (err.response?.status === 409) {
                    showNotification("Uživatel s tímto emaile již existuje", "error");
                } else {
                    showNotification('Nepodařilo se upravit uživatele', 'error');
                }
            });
    };

    const updateEnabled = () => {
        if (enabled === user?.enabled) return;
        putFetcher(`/users/${userId}/enable`, {enabled})
            .then(() => showNotification("Stav uživatele byl aktualizován", "success"))
            .catch(() => showNotification("Nepodařilo se aktualizovat stav uživatele", "error"));
    };

    const handleDelete = () => {
        confirm({
            description: `Opravdu chcete smazat tohoto uživatele?`,
            title: "Smazat uživatele",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/users/${userId}`).then(() => {
                    showNotification('Uživatel byl smazán', 'success');
                    navigate('/users');
                }).catch(() => {
                    showNotification('Uživatele se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    };

    const handleResetPassword = () => {
        confirm({
            description: `Opravdu chcete resetovat heslo uživatele ${user?.username}?`,
            title: "Resetovat heslo",
            confirmationText: "Resetovat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                postFetcher("/auth/reset-password-request", {email: user?.email})
                    .then(() => {
                        showNotification("Žádost o obnovu hesla byla odeslána", "success");
                    })
                    .catch(error => {
                        console.error(error);
                        if (error.response?.status === 404) {
                            showNotification("Uživatelský účet s takovýmto emailem neexistuje", "error");
                        } else {
                            showNotification("Došlo k chybě při odesílání žádosti o obnovu hesla", "error");
                        }
                    });
            })
            .catch(() => {
            });
    }

    if (isLoading || !user) return <Loading/>;
    if (error) return <ErrorMessage/>;

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                Detail uživatele
            </Typography>
            <Box component="form" noValidate>
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Uživatelské jméno"
                        variant="standard"
                        required
                        {...register('username')}
                        error={!!errors.username}
                        helperText={errors.username?.message}
                        InputProps={{
                            readOnly: true,
                        }}
                    />
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            fullWidth
                            label="Jméno"
                            variant="standard"
                            required
                            {...register('firstName')}
                            error={!!errors.firstName}
                            helperText={errors.firstName?.message}
                            InputProps={{
                                readOnly: noEdit,
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Příjmení"
                            variant="standard"
                            required
                            {...register('lastName')}
                            error={!!errors.lastName}
                            helperText={errors.lastName?.message}
                            InputProps={{
                                readOnly: noEdit,
                            }}
                        />
                    </Stack>
                    <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                        <TextField
                            fullWidth
                            label="Email"
                            type="email"
                            variant="standard"
                            required
                            {...register('email')}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            InputProps={{
                                readOnly: noEdit,
                            }}
                        />
                        <TextField
                            fullWidth
                            label="Telefon"
                            variant="standard"
                            {...register('phoneNumber')}
                            error={!!errors.phoneNumber}
                            helperText={errors.phoneNumber?.message}
                            InputProps={{
                                readOnly: noEdit,
                            }}
                        />
                    </Stack>
                    <Typography gutterBottom>
                        <RolesSelector selectedRoles={roles} setRoles={setRoles} readOnly={noEdit}/>
                    </Typography>
                    <Stack direction='row' spacing={2}>
                        <Typography>
                            Stav účtu:
                        </Typography>
                        <Chip
                            label={enabled ? 'Aktivní' : 'Neaktivní'}
                            title="Změnit stav účtu"
                            color={enabled ? 'success' : 'error'}
                            sx={{margin: '5px'}}
                            deleteIcon={<SettingsIcon/>}
                            onDelete={noEdit ? undefined : () => setEnabled(!enabled)}
                        />
                    </Stack>
                    {!readOnly && (
                        editMode ? (
                            <Stack direction='row' spacing={2}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{width: 250}}
                                    onClick={() => {
                                        setEditMode(false);
                                        resetData();
                                    }}
                                >
                                    Zrušit změny
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Uložit
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                                   spacing={2}>
                                <Button
                                    variant="outlined"
                                    color="error"
                                    sx={{px: 10}}
                                    onClick={handleDelete}
                                >
                                    Smazat
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => setEditMode(true)}
                                >
                                    Upravit
                                </Button>
                                <Button
                                    fullWidth
                                    variant="outlined"
                                    onClick={handleResetPassword}
                                >
                                    Resetovat heslo
                                </Button>
                            </Stack>
                        )
                    )}
                </Stack>
            </Box>
        </Paper>
    );
};

export default UserDetails;